
import { defineCommand } from '../../../concepts/commands/CommandDefinition';
import { inCommands } from '../../../concepts/commands/CommandRegistry';
import { ObjectManager } from '../../../concepts/objects';
import { Camera } from '../../canvas/Camera';
import { resolveSubjects } from '../../canvas/commands/CommandUtil';
import { EditorSession } from '../../canvas/EditorSession';
import { Physical } from '../../canvas/entities/Physical';
import { Physicals } from '../../canvas/entities/Physicals';
import { MyProfileInfo } from '../../canvas/MyProfileInfo';
import { PointerInfo } from '../../canvas/PointerInfo';
import { Selection } from '../../canvas/Selection';
import { SpatialIndex } from '../../canvas/utilities/SpatialIndex';
import { EffectDirector, Effects } from '../../canvas/visualization/EffectDirector';
import { findAndUpdatePhase, resolveActiveMeetingAndPhase } from '../../canvas/workflow/MeetingUtil';
import { SetupPhaseModel } from '../../canvas/workflow/SetupPhaseModel';
import { Resolver } from '../../Container';
import { ToastHub } from '../toast/ToastHub';
import { Concealable } from './Concealable';
import { ConcealmentIndex } from './ConcealmentIndex';
import { ConcealmentInfo } from './ConcealmentInfo';

const resolveSubjectsOrTarget = (r: Resolver, targetIds?: string | string[]) => {
    const resolved = resolveSubjects(r, targetIds);
    if (resolved.length) return resolved;
    const mpt = r.call(Camera).toModel(r.call(EditorSession).get(PointerInfo).position);
    return [Physicals.highestOf(r.call(SpatialIndex).test(mpt, Physical))].filter(x => !!x);
};

export const flip = defineCommand(
    'flip',
    'Flip',
    (r, [targetIds]: [string[]?]) => {

        const effects = r.call(EffectDirector);
        const objects = r.call(ObjectManager);
        const me = r.call(EditorSession).get(MyProfileInfo);

        const subjects = Physicals.polarize(resolveSubjectsOrTarget(r, targetIds), Concealable);
        if (!subjects.length) return;

        objects.update(() => {
            for (const s of subjects) {
                if (s.userId === me.id) {
                    s.concealed = !s.concealed;
                    effects.trigger(Effects.create('flip', 500, { value: s.concealed }), s);
                    effects.send(Effects.create('flip', 500, { value: s.concealed, strong: true }), s);
                }
            }
        });

    }
);

const concealOrReveal = (value: boolean) => (r: Resolver) => {

    const session = r.call(EditorSession);
    const objects = r.call(ObjectManager);
    const index = r.call(ConcealmentIndex);
    const myId = r.call(EditorSession).get(MyProfileInfo).id;
    const toast = r.call(ToastHub);

    const subjects = index.lookup(myId, '*');
    if (!!subjects.size) {
        objects.update(() => {
            for (const s of subjects) {
                s.concealed = value;
            }
        });
    }

    session.patch(ConcealmentInfo, { mode: value ? 'private' : 'public' });
    toast.send(`ptpt:${value ? 'conceal' : 'reveal'}`, {});
};

export const concealAll = defineCommand(
    'concealAll',
    'Conceal All',
    concealOrReveal(true),
);

export const revealAll = defineCommand(
    'revealAll',
    'Reveal All',
    concealOrReveal(false),
);

export const hostRevealSelected = defineCommand(
    'hostRevealSelected',
    null,
    (r: Resolver) => {

        const objects = r.call(ObjectManager);
        const subjects = Physicals.polarize(r.call(EditorSession).get(Selection), Concealable);
        if (!subjects.length) return;

        objects.update(() => {
            for (const s of subjects) {
                s.concealed = false;
            }
        });
    }
);

export const concealmentToggle = defineCommand(
    'concealmentToggle',
    null,
    (cc, [enabled]: [boolean?]) => {

        const objects = cc.call(ObjectManager);
        const toast = cc.call(ToastHub);

        const [meeting, phase] = resolveActiveMeetingAndPhase(objects);
        if (!meeting || !phase) return;

        const model = SetupPhaseModel.createFromExisting(meeting, phase);
        enabled = enabled ?? !model.concealment;
        model.update({ concealment: enabled });

        findAndUpdatePhase(objects, model);

        toast.invokeAndSend('meeting:concealment', { state: enabled ? 'enabled' : 'disabled' });
    }
);

export const inConcealCommands = () => inCommands(
    flip, concealAll, revealAll, concealmentToggle
);

